import { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { refreshUserToken, swallowThunkRejection } from '@rsos/sinatra';
import useIsIRP from '@rsos/utils/useIsIRP';

const MINUTE = 60 * 1000;

/**
 * A pre-emptive threshold that will trigger a refresh before the token's
 * actual expiration time.
 * e.g: If the threshold is set to 1 minute, a token set to expire at 12pm,
 * will refresh one minute earlier at 11:59pm.
 */
const EXPIRATION_THRESHOLD = 5 * MINUTE;

const useRenewAccessToken = (
  org,
  expirationThreshold = EXPIRATION_THRESHOLD,
) => {
  const dispatch = useDispatch();

  const { isIRPmode } = useIsIRP();

  // Flag to track if a request to renew a token is in progress
  const isRenewingRef = useRef(false);

  // Flag to track the timer ID of a setTimeout if currently in progress
  const isTimingRef = useRef(0);

  const [nextTimerTick, setNextTimerTick] = useState(false);

  // See authReducer in sinatra
  const { token, refresh_token, tokenExpiration } = useSelector(
    state => state.sinatra.auth.tokens,
  );

  /**
   * Renews access token while setting ref flag when the request is in progress
   */
  const renew = useCallback(async () => {
    if (isRenewingRef.current || !refresh_token) {
      return;
    }
    isRenewingRef.current = true;
    try {
      await dispatch(refreshUserToken(refresh_token));
    } catch {
      swallowThunkRejection();
    } finally {
      isRenewingRef.current = false;
    }
  }, [dispatch, refresh_token]);

  // On component mount
  useEffect(() => {
    if (!org || !token) {
      return;
    }
    const currentTime = new Date().getTime();
    if (tokenExpiration - expirationThreshold <= currentTime) {
      // This code will only be executed if any setTimeouts in progress,
      // are not continued by the browser after suspended sessions due to
      // idleness or the computer going to sleep. This is a failsafe just in
      // case that doesn't happen.
      (async () => {
        await renew();
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the nextTimerTick toggles, invoke renew()
  useEffect(() => {
    if (isIRPmode) return;

    (async () => {
      await renew();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextTimerTick]);

  // When the tokenExpiration value changes, it means that a token
  // was fetched and set successfully into redux state, now schedule the next
  // timer tick() for the next token refresh.
  useEffect(() => {
    const currentTime = new Date().getTime();
    if (isTimingRef.current) {
      // If we already have a timer scheduled then bail.
      return;
    }

    // Schedule the next timer tick.
    const nextTick = tokenExpiration - expirationThreshold - currentTime;
    const timerId = setTimeout(() => {
      isTimingRef.current = 0;
      if (!isRenewingRef.current) {
        setNextTimerTick(prev => !prev);
      }
    }, nextTick);
    isTimingRef.current = timerId;
  }, [tokenExpiration, expirationThreshold]);

  return { token, isRenewingRef };
};

export default useRenewAccessToken;
